import { get, isArray, isEqual } from "lodash";

import { MIN_ARRAY_LENGTH_FOR_INNER_CALCULATION } from "../constants";
import type { IDropdownItem, ISelectListItem } from "../models";
import { isNumericString } from "./string.helpers";

export const unique = <T extends string | number | boolean>(
  array: T[],
): T[] => [...new Set(array)];

/** New array, ascending numeric order; does not mutate `source`. */
export const sortAscendingNumbers = (
  source: readonly number[] | Iterable<number>,
): number[] => Array.from(source).toSorted((a, b) => a - b);

export const getSortableValue = (
  label: string | number | null | undefined,
  isNumberPreferredSort: boolean,
): number | string => {
  if (!label) {
    return isNumberPreferredSort ? 0 : "";
  }

  if (typeof label === "string") {
    if (isNumberPreferredSort && isNumericString(label)) {
      return Number(label);
    }

    return label?.toLowerCase()?.trim();
  }

  return label;
};

export const sortFiltersFnFactory =
  (isNumberPreferredSort = false, desc = false) =>
  <T extends IDropdownItem | ISelectListItem>(a: T, b: T): number => {
    const aLabel = getSortableValue(a.label, isNumberPreferredSort);
    const bLabel = getSortableValue(b.label, isNumberPreferredSort);

    if (desc) {
      if (typeof aLabel === "string" && typeof bLabel === "string") {
        return bLabel.localeCompare(aLabel);
      }

      if (aLabel < bLabel) {
        return 1;
      }

      if (aLabel > bLabel) {
        return -1;
      }
    } else {
      if (typeof aLabel === "string" && typeof bLabel === "string") {
        return aLabel.localeCompare(bLabel);
      }

      if (aLabel < bLabel) {
        return -1;
      }

      if (aLabel > bLabel) {
        return 1;
      }
    }

    return 0;
  };

export const valuesStringSortCompare = (a: string, b: string): number => {
  if (a < b) {
    return -1;
  }

  if (a > b) {
    return 1;
  }

  return 0;
};

export const compareIncludedDropdown = <
  T extends IDropdownItem | ISelectListItem,
>(
  a: T,
  values?: T[],
): number => {
  return values?.some(x => x.value === a.value) ? 1 : -1;
};

export const sortArrayAlphabetically = <T extends object>(
  items: T[] | undefined,
  sortKey: keyof T,
): T[] => {
  // Return an empty array if items is undefined or has no elements
  if (!items?.length) {
    return [];
  }

  const itemsMutable = [...items];

  // Sort the items alphabetically based on the label property
  return itemsMutable.sort((a, b) => {
    const aSortValue = a[sortKey] as string;
    const bSortValue = b[sortKey] as string;

    const normalizedA = aSortValue?.toLowerCase() || "";
    const normalizedB = bSortValue?.toLowerCase() || "";

    return normalizedA.localeCompare(normalizedB);
  });
};

export const sortFilterDropdownArray = <
  T extends IDropdownItem | ISelectListItem,
>(
  items: T[] | undefined,
  values?: T[],
  isNumberPreferredSort = false,
  desc = false,
): T[] => {
  if (!items?.length) {
    return [];
  }

  const itemsMutable = [...items];
  const compareFn = sortFiltersFnFactory(isNumberPreferredSort, desc);

  if (!values?.length) {
    return itemsMutable.sort(compareFn);
  }

  return itemsMutable.sort((a, b) => {
    return (
      compareIncludedDropdown(b, values) - compareIncludedDropdown(a, values) ||
      compareFn(a, b)
    );
  });
};

export const normalizeToArrayValue = <T>(val: T | T[]): T[] => {
  if (!val) {
    return [];
  }

  if (!isArray(val)) {
    return [val];
  }

  return val;
};

export const excludeNotArrayValue = <T = unknown>(val: unknown): T[] => {
  if (!val || !isArray(val)) {
    return [];
  }

  return val as T[];
};

export const isEqualNumberArraysAnyOrder = (
  arr1: number[],
  arr2: number[],
): boolean => {
  if (!isArray(arr1) || !isArray(arr2) || arr1.length !== arr2.length) {
    return false;
  }

  return isEqual([...arr1].sort(), [...arr2].sort());
};

export const isEqualStringArraysAnyOrder = (
  arr1: string[],
  arr2: string[],
): boolean => {
  if (!isArray(arr1) || !isArray(arr2) || arr1.length !== arr2.length) {
    return false;
  }

  return isEqual([...arr1].sort(), [...arr2].sort());
};

export const extractSingleValueFromArray = <T>(val: T | T[]): T | undefined => {
  if (!val) {
    return val;
  }

  if (isArray(val)) {
    return val.length ? val[0] : undefined;
  }

  return val;
};

export const packValueToArray = <T>(val: T | T[]): T[] => {
  if (!val) {
    return [];
  }

  if (isArray(val)) {
    return val;
  }

  return [val];
};

export const sortDropdownItemsByArray = <
  T extends IDropdownItem | ISelectListItem,
>(
  items: T[],
  topItems: number[],
): T[] => {
  if (!topItems?.length) {
    return items;
  }

  const existingTopItems = topItems.filter(topId =>
    items.some(item => item.value === topId),
  );

  return [...items].sort(
    (a, b) =>
      existingTopItems.findIndex(i => i === b.value) -
      existingTopItems.findIndex(i => i === a.value),
  );
};

export const stringifyDropdownItems = <
  T extends IDropdownItem | ISelectListItem,
>(
  items: T[],
): string => {
  const labels = items.map(item => item.label);

  return labels.join(", ");
};

export const arrayIncludesAllNumbers = (
  baseArray: number[],
  checkArray: number[],
): boolean => {
  if (!checkArray?.length) {
    return false;
  }
  const baseSet = new Set(baseArray);
  return checkArray.every(num => baseSet.has(Number(num)));
};

/**
 * Checks if the subArray is contained within the mainArray in the same order.
 * @param mainArray - The larger array in which to search.
 * @param subArray - The smaller array to search for.
 * @returns True if subArray is contained in mainArray in the same order, otherwise false.
 */
export const arrayIncludesAllNumbersOrdered = (
  mainArray: number[],
  subArray: number[],
): boolean => {
  if (subArray.length === 0) {
    return true;
  } // An empty subArray is always contained.

  if (mainArray.length < subArray.length) {
    return false;
  } // If the main array is smaller, it can't contain the subArray.

  let subArrayIndex = 0;

  for (const num of mainArray) {
    if (num === subArray[subArrayIndex]) {
      subArrayIndex++;
    }

    if (subArrayIndex === subArray.length) {
      return true;
    }
  }

  return false;
};

export const mergeNumericArrays = (
  arr1: number[] | undefined,
  arr2: number[] | undefined,
): number[] => {
  return [...new Set([...(arr1 || []), ...(arr2 || [])])];
};

/** Narrowing guard: a real array with at least one element. */
export const isNonEmptyArray = <T = unknown>(value: unknown): value is T[] =>
  Array.isArray(value) && value.length > 0;

export const isEmptyArray = (value: unknown): boolean =>
  Array.isArray(value) && value.length === 0;

export const isEmptyOrBlankStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) &&
  value.every(item => typeof item === "string" && item.trim() === "");

export const isEmptyObject = (value: unknown): boolean =>
  value !== null &&
  typeof value === "object" &&
  Object.keys(value as object).length === 0;

export const onlyNumericArrayValues = (
  items: (string | number)[] | undefined,
): number[] => {
  if (!items?.length) {
    return [];
  }

  return items?.map(item => Number(item)).filter(tag => tag);
};

export const getInnerArray = <T>(arr: T[]): T[] => {
  return arr.length >= MIN_ARRAY_LENGTH_FOR_INNER_CALCULATION
    ? arr.slice(1, -1)
    : [];
};

export const mergeArrays = <T>(...arrays: (T[] | null | undefined)[]): T[] => {
  return arrays.reduce<T[]>((acc, array) => {
    if (array?.length) {
      acc.push(...array);
    }

    return acc;
  }, []);
};

export const isSubsetArray = (
  innerArray: string[],
  parentArray: string[],
): boolean => {
  const setParent = new Set(parentArray);
  return innerArray.every(item => setParent.has(item));
};

export const areAllItemsNumericStrings = <T extends object>(
  items: T[],
  propertyKey?: keyof T,
): boolean => {
  if (!items?.length) {
    return false;
  }

  return items.every(item => {
    const currentPropertyCheck = propertyKey ? get(item, propertyKey) : item;
    return isNumericString(currentPropertyCheck as string);
  });
};

const MIN_ARRAY_LENGTH_FOR_INSERT_BEFORE_LAST = 2;

export const insertBeforeLast = <T>(
  originalArray: T[] | undefined,
  insertedElements: T[],
): T[] => {
  if (!originalArray?.length) {
    return insertedElements || [];
  }

  if (!insertedElements?.length) {
    return originalArray || [];
  }

  if (originalArray.length < MIN_ARRAY_LENGTH_FOR_INSERT_BEFORE_LAST) {
    return [...insertedElements, ...(originalArray || [])];
  }

  return [
    ...originalArray.slice(0, -1),
    ...insertedElements,
    originalArray[originalArray.length - 1],
  ];
};

export const areArraysExactlySame = <T extends number | string>(
  arr1: T[],
  arr2: T[],
): boolean => {
  if (!arr1?.length && !arr2?.length) {
    return true;
  }

  if (arr1.length !== arr2.length) {
    return false;
  }

  return arr1.every((e, i) => arr2[i] === e);
};

export const removeConsecutiveDuplicates = <T>(array: T[]): T[] => {
  return array.filter(
    (item, index) => index === 0 || item !== array[index - 1],
  );
};

export const isSubset = (required?: number[], allowed?: number[]): boolean => {
  if (!required?.length || !allowed?.length) {
    return true;
  }
  const allowedSet = new Set(allowed);
  return required.every(id => allowedSet.has(id));
};

export const setsEqual = (a: Set<number>, b: Set<number>): boolean =>
  a.size === b.size && [...a].every(x => b.has(x));

/** A copy of `items` with `item` removed when present, appended when not. */
export const toggleArrayItem = <T>({
  items,
  item,
}: {
  items: T[];
  item: T;
}): T[] =>
  items.includes(item)
    ? items.filter(current => current !== item)
    : [...items, item];

/** A new set with `item` removed when present, added when not. */
export const toggleSetItem = <T>({
  items,
  item,
}: {
  items: Set<T>;
  item: T;
}): Set<T> => {
  const next = new Set(items);
  if (next.has(item)) {
    next.delete(item);
  } else {
    next.add(item);
  }
  return next;
};

/** Label per numeric id. A string value is read as a number; empty options and nameless items are skipped. */
export const buildDropdownLabelByIdMap = (
  items: Pick<IDropdownItem, "value" | "label">[],
): Map<number, string> => {
  const labelById = new Map<number, string>();
  items.forEach(item => {
    const id = Number(item.value);
    if (id && item.label) {
      labelById.set(id, item.label);
    }
  });
  return labelById;
};
